import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { useDebouncedValue } from "../../../shared/application/useDebouncedValue";
import type {
  GeofenceLookupQuery,
  GeofenceLookupRepository,
} from "../../../contracts/geofenceOptions";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface GeofenceOption {
  readonly value: string;
  readonly label: string;
}

export interface UseGeofenceOptionsResult {
  readonly options: readonly GeofenceOption[];
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly isLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
}

/**
 * Server-searched, paginated geofence options for the `Combobox` async mode --
 * mirrors useBranchOptions. The API scopes the list to the caller's company.
 */
export function useGeofenceOptions(
  repository: GeofenceLookupRepository | undefined,
): UseGeofenceOptionsResult {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const search = debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  const listQuery = (page: number): GeofenceLookupQuery => ({
    page,
    limit: PAGE_SIZE,
    ...(search === undefined ? {} : { search }),
  });

  const query = useInfiniteQuery({
    queryKey: ["geofences", "options", { limit: PAGE_SIZE, search }],
    queryFn: ({ pageParam }) => repository!.list(listQuery(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.currentPage < lastPage.meta.totalPages
        ? lastPage.meta.currentPage + 1
        : undefined,
    enabled: repository !== undefined,
  });

  const options = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        page.data.map((geofence) => ({ value: geofence.id, label: geofence.name })),
      ),
    [query.data],
  );

  return {
    options,
    searchValue: searchTerm,
    onSearchChange: setSearchTerm,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    onLoadMore: () => {
      void query.fetchNextPage();
    },
  };
}
